import { Button, InputBaseProps, Textarea, TextInput } from '@mantine/core';
import { Form, useForm } from '@mantine/form';
import { createServerFn } from '@tanstack/react-start';
import { useMutation } from '@tanstack/react-query';
import { createTransport } from 'nodemailer';

type ContactFormProps = {
	className?: string;
	textInputProps?: Omit<InputBaseProps, 'label' | 'placeholder' | 'withAsterisk' | 'error' | 'onChange' | 'value'>;
};

type ContactFormData = {
	name: string;
	company?: string;
	email: string;
	phone: string;
	message: string;
};

const submitContactForm = createServerFn({
	method: 'POST',
	response: 'data',
})
	.validator((data) => data as ContactFormData)
	.handler((ctx) => {
		const { data } = ctx;
		const transport = createTransport({
			// @ts-ignore
			pool: false,
			host: 'smtp.gmail.com',
			secure: true,
			port: 465,
			priority: 'high',
			auth: {
				user: process.env.SMTP_USER || '',
				pass: process.env.SMTP_PASS || '',
			},
		});
		return transport
			.sendMail({
				from: process.env.SMTP_USER,
				to: process.env.SMTP_USER,
				subject: `Contact Form Submission from ${data.name}`,
				replyTo: data.email,
				text: `Name: ${data.name}\nCompany: ${data.company || 'N/A'}\nEmail: ${data.email}\nPhone: ${data.phone}\nMessage: ${data.message}`,
				html: `<p><strong>Name:</strong> ${data.name}</p>
				<p><strong>Company:</strong> ${data.company || 'N/A'}</p>
				<p><strong>Email:</strong> ${data.email}</p>
				<p><strong>Phone:</strong> ${data.phone}</p>
				<p><strong>Message:</strong></p>
				<p>${data.message}</p>`,
			})
			.then((info) => {
				console.log('Email sent:', info);
				return { success: true, message: 'Email sent successfully' };
			});
	});

export const ContactForm = ({ className, textInputProps }: ContactFormProps) => {
	const form = useForm<ContactFormData>({
		initialValues: {
			name: '',
			company: '',
			email: '',
			phone: '',
			message: '',
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
		validate: {
			name: (value) => (value.length < 2 ? 'Name must be at least 2 characters long' : null),
			email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email address'),
			phone: (value) => (/^\d{10}$/.test(value) ? null : 'Phone number must be 10 digits'),
			message: (value) => (value.length < 10 ? 'Message must be at least 10 characters long' : null),
		},
	});
	const { mutateAsync, isPending } = useMutation({
		mutationFn: (values: ContactFormData) => submitContactForm({ data: values }),
		onSuccess: (data, variables, context) => {
			console.log('Form submitted successfully:', data, variables, context);
		},
	});

	return (
		<Form
			className={className}
			form={form}
			onSubmit={mutateAsync}>
			<TextInput
				{...textInputProps}
				label="Name"
				placeholder="Your Name"
				withAsterisk
				{...form.getInputProps('name')}
			/>
			<TextInput
				{...textInputProps}
				label="Company"
				placeholder="Your Company"
				{...form.getInputProps('company')}
			/>
			<TextInput
				{...textInputProps}
				label="Email"
				placeholder="Your Email"
				withAsterisk
				{...form.getInputProps('email')}
			/>
			<TextInput
				{...textInputProps}
				label="Phone"
				placeholder="Your Phone Number"
				withAsterisk
				{...form.getInputProps('phone')}
			/>
			<Textarea
				{...textInputProps}
				label="Message"
				placeholder="Your Message"
				withAsterisk
				{...form.getInputProps('message')}
			/>
			<Button
				loading={isPending}
				type="submit">
				Send Message
			</Button>
		</Form>
	);
};
